import { z } from "zod";

// =====================================================================
// Relato de erros — contrato compartilhado (API + SPA).
//
// O objetivo do módulo NÃO é "guardar uma reclamação". É produzir um
// TICKET ACIONÁVEL: alguém do time de desenvolvimento abre, entende o que
// aconteceu e sabe onde mexer, sem precisar de uma segunda conversa com
// quem relatou. Um relato só vale se responde, sozinho:
//
//   1. O que a pessoa esperava e o que aconteceu?      (título/descrição)
//   2. Como chegar lá de novo?                          (passos)
//   3. O que a tela mostrava naquele instante?          (captura)
//   4. O sistema reclamou de alguma coisa?              (erros de API/JS)
//   5. Onde no código isso provavelmente mora?          (pista de origem)
//
// Os itens 2 a 5 são coletados pelo portal — não pela pessoa. Pedir "passos
// para reproduzir" a um operador de logística é pedir que ele faça o
// trabalho de triagem do time; o resultado costuma ser "não funciona".
// =====================================================================

// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------

/**
 * Severidade descrita pelo IMPACTO NO TRABALHO, não por jargão técnico.
 * "Crítico/Maior/Menor" obriga quem relata a traduzir a própria dor para a
 * escala do time; "me impede de trabalhar" ele responde sem hesitar — e é
 * exatamente o dado que o time precisa para priorizar.
 */
export const ISSUE_SEVERITIES = ["bloqueia", "atrapalha", "cosmetico"] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];

export const ISSUE_SEVERITY_META: Record<
  IssueSeverity,
  { label: string; pergunta: string; prioridade: "P1" | "P2" | "P3"; sla: string; tone: "danger" | "warning" | "info" }
> = {
  bloqueia: {
    label: "Me impede de trabalhar",
    pergunta: "Não consigo continuar a tarefa de jeito nenhum.",
    prioridade: "P1",
    sla: "mesmo dia",
    tone: "danger",
  },
  atrapalha: {
    label: "Atrapalha, mas consigo seguir",
    pergunta: "Dá para contornar, mas custa tempo ou gera retrabalho.",
    prioridade: "P2",
    sla: "até 3 dias úteis",
    tone: "warning",
  },
  cosmetico: {
    label: "Detalhe visual ou texto errado",
    pergunta: "Não afeta o resultado, mas está errado na tela.",
    prioridade: "P3",
    sla: "próxima entrega",
    tone: "info",
  },
};

/**
 * Fluxo de triagem. `nao_reproduz` e `duplicado` existem para que o time
 * possa FECHAR sem resolver e ainda assim dar uma resposta honesta a quem
 * relatou — sem esses estados, chamados irreproduzíveis apodrecem em
 * "aberto" e a fila deixa de significar alguma coisa.
 */
export const ISSUE_STATUSES = [
  "aberto",
  "triagem",
  "em_andamento",
  "resolvido",
  "nao_reproduz",
  "duplicado",
] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export const ISSUE_STATUS_META: Record<
  IssueStatus,
  { label: string; descricao: string; aberto: boolean; tone: "danger" | "warning" | "info" | "neutral" | "success" }
> = {
  aberto: { label: "Aberto", descricao: "Chegou e ainda não foi olhado.", aberto: true, tone: "danger" },
  triagem: { label: "Em triagem", descricao: "Alguém está reproduzindo e dimensionando.", aberto: true, tone: "warning" },
  em_andamento: { label: "Em andamento", descricao: "Correção em desenvolvimento.", aberto: true, tone: "info" },
  resolvido: { label: "Resolvido", descricao: "Corrigido e publicado.", aberto: false, tone: "success" },
  nao_reproduz: { label: "Não reproduz", descricao: "Não foi possível repetir o problema.", aberto: false, tone: "neutral" },
  duplicado: { label: "Duplicado", descricao: "Já existe outro chamado para o mesmo problema.", aberto: false, tone: "neutral" },
};

export const isOpenStatus = (s: string): boolean => ISSUE_STATUS_META[s as IssueStatus]?.aberto ?? false;

// ---------------------------------------------------------------------
// Trilha de passos (breadcrumbs)
// ---------------------------------------------------------------------

export const BREADCRUMB_KINDS = ["navegacao", "clique", "campo", "api", "erro", "console"] as const;
export type BreadcrumbKind = (typeof BREADCRUMB_KINDS)[number];

export interface Breadcrumb {
  /** Instante do passo (epoch ms). */
  t: number;
  kind: BreadcrumbKind;
  /** Texto curto e legível em pt-BR — é o que vira "passo" no ticket. */
  label: string;
  /** Complemento técnico (URL, status HTTP, mensagem do erro). */
  detail?: string;
  /** `false` marca o passo que FALHOU. É o que o time lê primeiro. */
  ok?: boolean;
}

export const breadcrumbSchema = z.object({
  t: z.number().int().nonnegative(),
  kind: z.enum(BREADCRUMB_KINDS),
  label: z.string().min(1).max(300),
  detail: z.string().max(2000).optional(),
  ok: z.boolean().optional(),
});

/** Teto da trilha: mantém o payload pequeno e o ticket legível. */
export const BREADCRUMB_LIMIT = 60;

/**
 * Chaves de query string que NUNCA entram num chamado. A trilha registra a
 * URL das chamadas de API porque o filtro aplicado costuma ser a diferença
 * entre "reproduzi" e "aqui funciona" — mas um `?token=...` copiado para um
 * ticket é uma credencial vazando para um canal que não foi feito para ela.
 */
const SEGREDOS = /^(token|access|refresh|refreshtoken|accesstoken|password|senha|secret|key|apikey|authorization)$/i;

/** Remove valores sensíveis da query string, preservando as chaves (o filtro importa). */
export function sanitizeUrl(url: string): string {
  const [caminho, query] = url.split("?");
  if (!query) return caminho;
  const partes = query.split("&").map((par) => {
    const [k, ...resto] = par.split("=");
    return SEGREDOS.test(decodeURIComponent(k)) ? `${k}=«omitido»` : `${k}=${resto.join("=")}`;
  });
  return `${caminho}?${partes.join("&")}`;
}

/**
 * Colapsa repetições consecutivas do mesmo passo ("Clicou em «Próxima» ×7").
 * Sem isso, rolar uma grade grande enche a trilha inteira com o mesmo clique
 * e empurra para fora justamente o passo que causou o erro.
 */
export function collapseBreadcrumbs(trilha: Breadcrumb[]): (Breadcrumb & { vezes: number })[] {
  const out: (Breadcrumb & { vezes: number })[] = [];
  for (const b of trilha) {
    const ultimo = out[out.length - 1];
    if (ultimo && ultimo.kind === b.kind && ultimo.label === b.label && ultimo.ok === b.ok) {
      ultimo.vezes += 1;
      ultimo.t = b.t; // mantém o instante da ÚLTIMA ocorrência
      continue;
    }
    out.push({ ...b, vezes: 1 });
  }
  return out;
}

const ICONE: Record<BreadcrumbKind, string> = {
  navegacao: "→",
  clique: "•",
  campo: "✎",
  api: "⇄",
  erro: "✗",
  console: "!",
};

/** Uma linha de passo, pronta para o ticket. */
export function describeBreadcrumb(b: Breadcrumb & { vezes?: number }): string {
  const vezes = b.vezes && b.vezes > 1 ? ` (×${b.vezes})` : "";
  const falha = b.ok === false ? " ⟵ FALHOU" : "";
  const detalhe = b.detail ? ` — ${b.detail}` : "";
  return `${ICONE[b.kind]} ${b.label}${vezes}${detalhe}${falha}`;
}

/** Passos numerados, colapsados e legíveis. É a seção "como reproduzir". */
export function reproSteps(trilha: Breadcrumb[]): string[] {
  return collapseBreadcrumbs(trilha).map((b, i) => `${i + 1}. ${describeBreadcrumb(b)}`);
}

/**
 * O passo que interessa: o último que falhou. Um chamado começa a ser lido
 * pelo fim — o time quer saber o que quebrou antes de saber como se chegou lá.
 */
export function primeiraFalha(trilha: Breadcrumb[]): Breadcrumb | null {
  for (let i = trilha.length - 1; i >= 0; i--) if (trilha[i].ok === false) return trilha[i];
  return null;
}

// ---------------------------------------------------------------------
// Pista de origem: rota do portal → arquivos prováveis
// ---------------------------------------------------------------------

/**
 * Mapa rota → arquivos que provavelmente contêm o defeito. Não é mágica:
 * é o conhecimento que o time já tem, escrito UMA vez, para que o ticket
 * chegue com o ponto de partida em vez de exigir uma arqueologia por
 * chamado. Quando uma rota nova nascer sem entrada aqui, o ticket
 * simplesmente omite a seção — degrada, não mente.
 */
export const ROUTE_SOURCE_HINTS: { prefix: string; area: string; files: string[] }[] = [
  { prefix: "/torre", area: "Torre de Controle", files: ["packages/web/src/pages/Torre.tsx", "packages/api/src/http/routes/operacoes.routes.ts", "packages/api/src/services/operacoes.service.ts"] },
  { prefix: "/operacoes", area: "Operações", files: ["packages/web/src/pages/Operacoes.tsx", "packages/api/src/http/routes/operacoes.routes.ts", "packages/api/src/services/operacoes.service.ts"] },
  { prefix: "/programacao", area: "Programação (DATA)", files: ["packages/web/src/pages/Programacao.tsx", "packages/api/src/http/routes/ingest.routes.ts", "packages/api/src/ingest/dataIngest.ts"] },
  { prefix: "/cadastros", area: "Cadastros", files: ["packages/web/src/pages/Cadastros.tsx", "packages/web/src/components/FormEngine.tsx", "packages/api/src/http/routes/entities.routes.ts", "packages/shared/src/catalog.ts"] },
  { prefix: "/metas", area: "Metas", files: ["packages/web/src/pages/Metas.tsx", "packages/api/src/http/routes/metas.routes.ts", "packages/api/src/services/metas.service.ts", "packages/shared/src/metas.ts"] },
  { prefix: "/insights", area: "Insights", files: ["packages/web/src/pages/Insights.tsx", "packages/api/src/http/routes/insights.routes.ts", "packages/api/src/services/insights.service.ts"] },
  { prefix: "/explorar", area: "Explorar com IA", files: ["packages/web/src/pages/ExplorarIA.tsx", "packages/web/src/lib/explorerStore.ts", "packages/api/src/services/agent.service.ts"] },
  { prefix: "/inconsistencias", area: "Inconsistências", files: ["packages/web/src/pages/Inconsistencias.tsx", "packages/api/src/http/routes/insights.routes.ts", "packages/api/src/services/insights.service.ts"] },
  { prefix: "/agente", area: "Agente IA", files: ["packages/web/src/pages/Agente.tsx", "packages/web/src/components/AgentChat.tsx", "packages/api/src/services/agent.service.ts"] },
  { prefix: "/chamados", area: "Chamados", files: ["packages/web/src/pages/Chamados.tsx", "packages/api/src/http/routes/issues.routes.ts", "packages/api/src/services/issues.service.ts"] },
  { prefix: "/atualizacoes", area: "Atualizações", files: ["packages/web/src/pages/Atualizacoes.tsx", "scripts/generate-updates.mjs"] },
  { prefix: "/entrega", area: "Acompanhamento", files: ["packages/web/src/pages/Entrega.tsx"] },
  { prefix: "/admin", area: "Administração", files: ["packages/web/src/pages/Admin.tsx", "packages/api/src/http/routes/admin.routes.ts"] },
  { prefix: "/trocar-senha", area: "Troca de senha", files: ["packages/web/src/pages/TrocarSenha.tsx", "packages/api/src/http/routes/auth.routes.ts"] },
  { prefix: "/login", area: "Login", files: ["packages/web/src/pages/Login.tsx", "packages/api/src/http/routes/auth.routes.ts"] },
  { prefix: "/", area: "Dashboard", files: ["packages/web/src/pages/Dashboard.tsx", "packages/api/src/http/routes/insights.routes.ts"] },
];

export function sourceHintForRoute(route: string): { area: string; files: string[] } | null {
  const caminho = (route || "/").split("?")[0];
  // Prefixos mais longos primeiro: "/cadastros/frota" deve casar com
  // "/cadastros", não com "/" (que casa com tudo e viria antes na ordem natural).
  const achado = [...ROUTE_SOURCE_HINTS]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((h) => caminho === h.prefix || caminho.startsWith(h.prefix === "/" ? "/" : `${h.prefix}/`) || caminho === h.prefix);
  return achado ? { area: achado.area, files: achado.files } : null;
}

// ---------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------

export const issueClientSchema = z.object({
  userAgent: z.string().max(500).default(""),
  viewport: z.string().max(40).default(""),
  screen: z.string().max(40).default(""),
  language: z.string().max(40).default(""),
  timezone: z.string().max(80).default(""),
  /** Versão do bundle (VITE_APP_VERSION) — sem isto não dá para saber se o relato é de código já corrigido. */
  appVersion: z.string().max(60).default("dev"),
});
export type IssueClient = z.infer<typeof issueClientSchema>;

export const createIssueSchema = z.object({
  title: z.string().trim().min(8, "Descreva o problema em pelo menos 8 caracteres").max(160),
  description: z.string().trim().max(4000).default(""),
  /** O que a pessoa ESPERAVA. Separado da descrição porque é a pergunta que o time mais faz de volta. */
  expected: z.string().trim().max(2000).default(""),
  severity: z.enum(ISSUE_SEVERITIES),
  route: z.string().max(300).default("/"),
  breadcrumbs: z.array(breadcrumbSchema).max(BREADCRUMB_LIMIT).default([]),
  client: issueClientSchema.default({}),
  /** Captura da tela em data URL (image/webp ou image/png). Opcional: a pessoa pode recusar. */
  screenshot: z
    .string()
    .regex(/^data:image\/(png|webp|jpeg);base64,[A-Za-z0-9+/=]+$/, "Captura inválida")
    .max(6_000_000)
    .optional(),
});
export type CreateIssueInput = z.infer<typeof createIssueSchema>;

export const updateIssueSchema = z
  .object({
    status: z.enum(ISSUE_STATUSES).optional(),
    assignee: z.string().trim().max(120).nullable().optional(),
    devNotes: z.string().trim().max(8000).optional(),
    duplicateOf: z.number().int().positive().nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Informe ao menos um campo");
export type UpdateIssueInput = z.infer<typeof updateIssueSchema>;

// ---------------------------------------------------------------------
// Registro completo (o que a API devolve)
// ---------------------------------------------------------------------

export interface Issue {
  id: number;
  code: string;
  title: string;
  description: string;
  expected: string;
  severity: IssueSeverity;
  status: IssueStatus;
  route: string;
  reporterEmail: string | null;
  reporterName: string | null;
  reporterRole: string | null;
  breadcrumbs: Breadcrumb[];
  client: IssueClient;
  hasScreenshot: boolean;
  assignee: string | null;
  devNotes: string;
  duplicateOf: number | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

/** Código humano estável: BN-000042. Curto o suficiente para caber num "olha o BN-42 aí". */
export const issueCode = (id: number): string => `BN-${String(id).padStart(6, "0")}`;

// ---------------------------------------------------------------------
// O ticket
// ---------------------------------------------------------------------

const dataHora = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

/**
 * Renderiza o chamado como Markdown pronto para colar em GitHub/Jira/Slack.
 *
 * A ordem das seções é deliberada e segue a leitura de quem vai CORRIGIR:
 * impacto → o que quebrou → onde mexer → como repetir → contexto. Colocar
 * "ambiente" no topo (como fazem os templates padrão) obriga o time a rolar
 * três telas antes de saber se o chamado é urgente.
 *
 * `baseUrl` opcional insere o link direto para a captura e para o chamado no
 * portal — sem ele o ticket continua válido, só sem os atalhos.
 */
export function formatTicketMarkdown(issue: Issue, baseUrl?: string): string {
  const sev = ISSUE_SEVERITY_META[issue.severity];
  const falha = primeiraFalha(issue.breadcrumbs);
  const origem = sourceHintForRoute(issue.route);
  const erros = issue.breadcrumbs.filter((b) => b.kind === "erro" || b.kind === "console");
  const apis = issue.breadcrumbs.filter((b) => b.kind === "api" && b.ok === false);
  const link = (p: string) => (baseUrl ? `${baseUrl.replace(/\/$/, "")}${p}` : null);

  const L: string[] = [];

  L.push(`# ${issue.code} — ${issue.title}`);
  L.push("");
  L.push(`**${sev.prioridade}** · ${sev.label} · alvo: ${sev.sla}`);
  L.push("");
  L.push(
    `| | |`,
    `|---|---|`,
    `| Situação | ${ISSUE_STATUS_META[issue.status].label} |`,
    `| Relatado por | ${issue.reporterName ?? "—"} (${issue.reporterEmail ?? "—"}${issue.reporterRole ? `, ${issue.reporterRole}` : ""}) |`,
    `| Quando | ${dataHora(issue.createdAt)} |`,
    `| Tela | \`${issue.route}\`${origem ? ` — ${origem.area}` : ""} |`,
    `| Versão | ${issue.client.appVersion || "dev"} |`,
  );
  L.push("");

  L.push("## O que aconteceu");
  L.push(issue.description.trim() || "_(sem descrição adicional — ver passos e captura)_");
  if (issue.expected.trim()) {
    L.push("");
    L.push("**Comportamento esperado:** " + issue.expected.trim());
  }
  L.push("");

  if (falha) {
    L.push("## Ponto da falha");
    L.push("```");
    L.push(describeBreadcrumb(falha));
    L.push("```");
    L.push("");
  }

  if (apis.length) {
    L.push("## Chamadas de API que falharam");
    for (const a of apis) L.push(`- \`${a.label}\` — ${a.detail ?? "sem detalhe"}`);
    L.push("");
  }

  if (erros.length) {
    L.push("## Erros no navegador");
    L.push("```");
    for (const e of erros) L.push(`${e.label}${e.detail ? `\n  ${e.detail}` : ""}`);
    L.push("```");
    L.push("");
  }

  if (origem) {
    L.push("## Por onde começar");
    L.push(`Área **${origem.area}**. Arquivos prováveis:`);
    for (const f of origem.files) L.push(`- \`${f}\``);
    L.push("");
  }

  L.push("## Passos até o problema");
  const passos = reproSteps(issue.breadcrumbs);
  if (passos.length) {
    L.push("_Trilha capturada automaticamente pelo portal (valores digitados não são gravados)._");
    L.push("");
    for (const p of passos) L.push(p);
  } else {
    L.push("_Nenhum passo capturado (relato aberto logo após carregar a página)._");
  }
  L.push("");

  L.push("## Ambiente");
  L.push(`- Navegador: \`${issue.client.userAgent || "—"}\``);
  L.push(`- Janela: ${issue.client.viewport || "—"} (tela ${issue.client.screen || "—"})`);
  L.push(`- Idioma / fuso: ${issue.client.language || "—"} · ${issue.client.timezone || "—"}`);
  L.push("");

  if (issue.hasScreenshot) {
    const url = link(`/api/v1/issues/${issue.id}/screenshot`);
    L.push("## Captura de tela");
    L.push(url ? `![Captura do momento do erro](${url})` : "_Disponível no portal, em Chamados._");
    L.push("");
  }

  if (issue.devNotes.trim()) {
    L.push("## Notas do time");
    L.push(issue.devNotes.trim());
    L.push("");
  }

  const portal = link(`/chamados?id=${issue.id}`);
  if (portal) L.push(`---`, `[Abrir ${issue.code} no portal](${portal})`);

  return L.join("\n");
}

/** Linha única para listas e notificações: "BN-000042 · P1 · Torre — grade vazia". */
export function issueOneLiner(issue: Issue): string {
  return `${issue.code} · ${ISSUE_SEVERITY_META[issue.severity].prioridade} · ${
    sourceHintForRoute(issue.route)?.area ?? issue.route
  } — ${issue.title}`;
}
